import loadable from "@loadable/component";
import classnames from "classnames";
import { inject } from "mobx-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Store } from "../../../../client/stores";
import type { BlockFragment, PodcastFragment } from "../../generated/webapi.g";
import { reverseRoute } from "../../lib/route";
import { getPodcastDisplayHints } from "../../services/Sites.utils";
import { Container, Grid, Heading } from "../../ui";
import "./Podcasts.style.scss";

const PodcastsCollection = loadable(() => import("./PodcastsCollection.component"));
const PodcastTile = loadable(() => import("./PodcastTile.component"));

interface PodcastsProps {
    block: BlockFragment;
    podcasts: PodcastFragment[];
    store?: Store;
}

export const Podcasts = inject("store")(({ block, podcasts, store }: PodcastsProps) => {
    if (!podcasts || !store) {
        return null;
    }

    const { tags } = block;
    const { site, env } = store;
    const { t } = useTranslation();
    const podcastLink = reverseRoute(site, "podcasts");
    useEffect(() => {
        store.storeBlock(block);
    });

    const css = classnames("component-podcasts", {
        [`themed-block`]: tags?.includes("display-hints/themed-block"),
    });

    const headingLink = {
        text: t("view_more"),
        label: `${t("view_podcast")}`,
        url: podcastLink || "/",
    };

    const displayHints = getPodcastDisplayHints(block.tags || []);

    if (displayHints.isPodcastCollection) {
        const title = `${site.sections.general?.name} Podcasts`;
        return <PodcastsCollection block={block} podcasts={podcasts} title={title} />;
    }

    // this will render homepage presentation showing only first 4 OR showing only one podcast
    return (
        <Container className={css}>
            <Heading level={2} link={displayHints.showViewMore ? headingLink : undefined}>
                Podcasts
            </Heading>
            <Grid columns={displayHints.columns}>
                {podcasts.slice(0, displayHints.columns).map((podcast, i) => (
                    <PodcastTile host={env.IMAGE_HOST} podcast={podcast} key={i} context={`podcast_${i}`} />
                ))}
            </Grid>
        </Container>
    );
});

export default Podcasts;
